/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import CommunicatorControl from '../../communicator/control/communicator-control';
import ErrorName from '../../error/error-name';

/**
 * @private
 * @typedef {Record<string, *>} ControlManager TODO #555
 * @typedef {import('../../communicator/settings/communicator-settings').CommunicatorSettingsGetter} CommunicatorSettingsGetter
 * @typedef {import('../../logger/logger').Logger} Logger
 */

/**
 * @typedef {Pick<import('../../content/info/content-info').ContentInfo, 'communicatorSettings'>} IContentInfo
 */

/**
 * Nahrazovač nastavení ovládání zařízení.
 * Stará se o omezení ovládání zařízení dle obsahu datového skladu.
 */
class ReplacerControl {
  /**
   * Vytvoří instanci.
   * @param {ControlManager} controlManager
   * @param {Logger} logger
   */
  constructor(controlManager, logger) {
    this.controlManager = controlManager;
    this.logger = logger;
  }

  /**
   * Provedení specifických reakcí na chyby při změně omezení ovládání.
   * Chyby související s podporou zařízení nejsou důležité
   * a zalogují se s nízkou prioritou.
   * V těchto případech se správce chová, jako by vše proběhlo bez problémů.
   * Ostatní chyby ošetřuje ten, kdo metodu vyvolal.
   * @param {Error} error
   * @private
   * @returns {void}
   */
  handleError(error) {
    // Ignorování chyb způsobených nepodporováním omezení ovládání.
    if (error.name === ErrorName.NotSupportedError) {
      return this.logger.warn(error);
    }
    throw error;
  }

  /**
   * Nahradí stávající nastavení ovládání dle obsahu zařízení.
   * @param {IContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise<void>} Vyřeší se po dokončení změny.
   */
  replace(contentInfo) {
    return this.replaceLocalLock(contentInfo.communicatorSettings).then(() =>
      this.replaceRemoteLock(contentInfo.communicatorSettings)
    );
  }

  /**
   * Nahradí zámek ovládání pro tlačítka umístěné přímo na zařízení.
   * @param {CommunicatorSettingsGetter} settings
   * @private
   * @returns {Promise<void>}
   */
  replaceLocalLock(settings) {
    return new Promise((resolve) => {
      const lock = CommunicatorControl.parseLocalLock(settings);
      if (lock === null) {
        return resolve();
      }
      return resolve(
        this.controlManager
          .setLocalLock(lock)
          .catch((error) => this.handleError(error))
      );
    });
  }

  /**
   * Nahradí zámek ovládání pro tlačítka na dálkovém ovládání.
   * @param {CommunicatorSettingsGetter} settings
   * @private
   * @returns {Promise<void>}
   */
  replaceRemoteLock(settings) {
    return new Promise((resolve) => {
      const lock = CommunicatorControl.parseRemoteLock(settings);
      if (lock === null) {
        return resolve();
      }
      return resolve(
        this.controlManager
          .setRemoteLock(lock)
          .catch((error) => this.handleError(error))
      );
    });
  }
}

export default ReplacerControl;
